import { toast } from 'sonner';
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../AuthContext';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import api from '../api';

function useContainerWidth(ref) {
  const [width, setWidth] = useState(300);
  useEffect(() => {
    const update = () => { if (ref.current) setWidth(ref.current.offsetWidth - 32); };
    update();
    window.addEventListener('resize', update);
    const timer = setTimeout(update, 200);
    return () => { window.removeEventListener('resize', update); clearTimeout(timer); };
  }, [ref]);
  return width;
}

function AdminDashboard() {
  const [metrics, setMetrics] = useState(null);
  const [loans, setLoans] = useState([]);
  const [lenders, setLenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bonusesSummary, setBonusesSummary] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const barChartRef = useRef(null);
  const barChartWidth = useContainerWidth(barChartRef);
  const pieChartRef = useRef(null);
  const pieChartWidth = useContainerWidth(pieChartRef);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [metricsRes, loansRes, lendersRes, bonusRes] = await Promise.all([
        api.get('/api/admin/dashboard/metrics'),
        api.get('/api/loans'),
        api.get('/api/admin/lenders').catch(() => ({ data: [] })),
        api.get('/api/admin/referral-bonuses-summary').catch(() => ({ data: null }))
      ]);
      setMetrics(metricsRes.data);
      setLoans(loansRes.data);
      setLenders(lendersRes.data);
      setBonusesSummary(bonusRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (loans.length === 0) { toast.info(t('messages.noDataToExport')); return; }
    const headers = ['Prestamista,Capital,Interes,Estado,Inicio,Vencimiento,Intereses Generados,Dias Restantes,Total Devolver'];
    const rows = loans.map(l =>
      `${l.lender_name || l.lender_id},${l.capital},${l.interest_rate}%,${l.status},${l.start_date?.slice(0,10)},${l.end_date?.slice(0,10)},${(l.interest_generated||0).toFixed(2)},${l.days_remaining||0},${(l.total_return||l.capital).toFixed(2)}`
    );
    const blob = new Blob([[headers, ...rows].join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `prestecs_vivendius_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
  };

  // === COMPUTED METRICS ===
  const activeLoans = loans.filter(l => l.status === 'active');
  const pendingLoans = loans.filter(l => l.status === 'pending_signature');

  const totalCapital = activeLoans.reduce((s, l) => s + (l.capital || 0), 0);
  const totalInterestGenerated = activeLoans.reduce((s, l) => s + (l.interest_generated || 0), 0);
  const totalToReturn = activeLoans.reduce((s, l) => s + (l.total_return || l.capital || 0), 0);
  const totalInterestPending = totalToReturn - totalCapital;
  const dailyInterestCost = activeLoans.reduce((s, l) => s + ((l.capital * l.interest_rate / 100) / 365), 0);
  const monthlyInterestCost = dailyInterestCost * 30;
  const totalBonuses = bonusesSummary?.total_bonuses_gross || 0;
  const totalDebt = totalToReturn + totalBonuses;

  // Sort by maturity (soonest first)
  const sortedActiveLoans = [...activeLoans].sort((a, b) => (a.days_remaining || 999) - (b.days_remaining || 999));
  const cancelledLoans = loans.filter(l => l.status === 'cancelled');
  const maturedLoans = loans.filter(l => l.status === 'matured');
  const nextMaturity = sortedActiveLoans[0];

  // Monthly cash flow projection
  const getMonthlyProjection = () => {
    const months = {};
    activeLoans.forEach(l => {
      if (!l.end_date) return;
      const d = new Date(l.end_date);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString(i18n.language === 'ca' ? 'ca-ES' : 'es-ES', { month: 'short', year: 'numeric' });
      if (!months[key]) months[key] = { key, label, capital: 0, interests: 0, total: 0, count: 0 };
      months[key].capital += l.capital || 0;
      months[key].interests += (l.total_return || l.capital) - l.capital;
      months[key].total += l.total_return || l.capital;
      months[key].count += 1;
    });
    return Object.values(months).sort((a, b) => a.key.localeCompare(b.key));
  };

  const statusData = loans.reduce((acc, l) => {
    const s = l.status;
    const label = s === 'active' ? t('status.active') : s === 'pending_signature' ? t('status.pending') : s === 'matured' ? t('status.matured') : s === 'cancelled' ? t('status.cancelled') : s;
    const color = s === 'active' ? '#007a33' : s === 'pending_signature' ? '#FF9800' : s === 'cancelled' ? '#ef4444' : '#103766';
    const existing = acc.find(x => x.name === label);
    if (existing) existing.value += 1;
    else acc.push({ name: label, value: 1, color });
    return acc;
  }, []);

  const locale = i18n.language === 'ca' ? 'ca-ES' : 'es-ES';
  const fmt = (n, dec = 0) => n?.toLocaleString(locale, { minimumFractionDigits: dec, maximumFractionDigits: dec }) || '0';

  if (loading) {
    return (
      <div className="main-layout">
        <Sidebar />
        <div className="content-area" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  const KPI = ({ label, value, sub, color, icon }) => (
    <div data-testid={`kpi-${label}`} style={{
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)', borderRadius: '16px', padding: '1.25rem',
      border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem', fontWeight: '500', marginBottom: '0.4rem' }}>{label}</p>
          <p style={{ fontSize: '1.6rem', fontWeight: '700', color: color || '#60a5fa', lineHeight: 1 }}>{value}</p>
        </div>
        <span style={{ fontSize: '1.3rem' }}>{icon}</span>
      </div>
      {sub && <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.7rem', marginTop: '0.4rem' }}>{sub}</p>}
    </div>
  );

  return (
    <div className="main-layout">
      <Sidebar />
      <div className="content-area">
        <TopBar title={t('admin.dashboard')} actions={
          <button onClick={exportToCSV} className="btn-secondary" data-testid="export-csv-button"
            style={{ padding: '0.6rem 1.2rem', background: 'linear-gradient(135deg, #007a33 0%, #00a843 100%)', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '600', fontSize: '0.85rem' }}>
            {t('buttons.exportCSV')}
          </button>
        } />

        <main style={{ padding: '1.5rem 2rem', maxWidth: '1500px' }} className="fade-in">

          {/* === ROW 1: KPI CARDS === */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
            <KPI label={t('admin.totalCapital')} value={`€${fmt(totalCapital)}`} sub={`${activeLoans.length} ${t('admin.activeLoans').toLowerCase()}`} color="#103766" icon="💰" />
            <KPI label={i18n.language === 'ca' ? 'Total a Tornar' : 'Total a Devolver'} value={`€${fmt(totalToReturn)}`} sub={i18n.language === 'ca' ? 'Capital + interessos' : 'Capital + intereses'} color="#dc2626" icon="🏦" />
            <KPI label={i18n.language === 'ca' ? 'Interessos Pendents' : 'Intereses Pendientes'} value={`€${fmt(totalInterestPending, 2)}`} sub={`€${fmt(monthlyInterestCost, 2)}/${i18n.language === 'ca' ? 'mes' : 'mes'}`} color="#f97316" icon="📊" />
            <KPI label={i18n.language === 'ca' ? 'Cost Diari' : 'Coste Diario'} value={`€${fmt(dailyInterestCost, 2)}`} sub={`${fmt(dailyInterestCost * 365, 2)}€/${i18n.language === 'ca' ? 'any' : 'año'}`} color="#7c3aed" icon="📅" />
            <KPI label={t('admin.totalLenders')} value={metrics?.total_lenders || 0} sub={`${pendingLoans.length} ${i18n.language === 'ca' ? 'pendents firma' : 'pendientes firma'}`} color="#2563eb" icon="👥" />
            {nextMaturity && (
              <KPI
                label={i18n.language === 'ca' ? 'Pròxim Venciment' : 'Próximo Vencimiento'}
                value={`${nextMaturity.days_remaining}d`}
                sub={`€${fmt(nextMaturity.total_return || nextMaturity.capital)} - ${nextMaturity.lender_name || ''}`}
                color={nextMaturity.days_remaining < 60 ? '#dc2626' : '#f97316'}
                icon="⏰"
              />
            )}
          </div>

          {/* === PENDING SIGNATURE ALERT (above everything) === */}
          {pendingLoans.length > 0 && (
            <div onClick={() => navigate('/admin/loans')} style={{
              background: 'linear-gradient(135deg, rgba(245,158,11,0.15) 0%, rgba(217,119,6,0.08) 100%)',
              border: '2px solid rgba(245,158,11,0.4)',
              borderRadius: '16px', padding: '1.25rem', marginBottom: '1.5rem', cursor: 'pointer',
              display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '1rem'
            }}>
              <div>
                <h3 style={{ fontSize: '1rem', fontWeight: '700', color: '#fbbf24', margin: '0 0 0.5rem 0' }}>
                  {i18n.language === 'ca' ? `${pendingLoans.length} Préstec(s) Pendent(s) de Firma` : `${pendingLoans.length} Préstamo(s) Pendiente(s) de Firma`}
                </h3>
                {pendingLoans.map(l => (
                  <p key={l._id} style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)', margin: '0.2rem 0' }}>
                    {l.lender_name || l.lender_id?.slice(-8)} — €{l.capital?.toLocaleString('es-ES')} al {l.interest_rate}%
                  </p>
                ))}
              </div>
              <span style={{ color: '#fbbf24', fontSize: '1.5rem' }}>&rarr;</span>
            </div>
          )}

          {/* === ROW 2: LOANS TABLE (maturity timeline) === */}
          {sortedActiveLoans.length > 0 && (
            <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '16px', padding: '1rem', marginBottom: '1.5rem', border: '1px solid rgba(255,255,255,0.08)' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '700', color: '#60a5fa', marginBottom: '1rem' }}>
                {i18n.language === 'ca' ? 'Calendari de Venciments' : 'Calendario de Vencimientos'}
              </h3>
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                {sortedActiveLoans.map((loan) => {
                  const daysLeft = loan.days_remaining || 0;
                  const urgency = daysLeft < 30 ? '#dc2626' : daysLeft < 90 ? '#f97316' : daysLeft < 180 ? '#eab308' : '#10b981';
                  const interest = (loan.total_return || loan.capital) - loan.capital;
                  return (
                    <div key={loan._id} onClick={() => navigate(`/loans/${loan._id}`)} style={{
                      padding: '1rem', background: 'rgba(255,255,255,0.03)', borderRadius: '12px',
                      border: '1px solid rgba(255,255,255,0.06)', cursor: 'pointer'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <span style={{ fontWeight: '700', color: '#60a5fa', fontSize: '0.95rem' }}>{loan.lender_name || loan.lender_id?.slice(-6)}</span>
                        <span style={{ background: urgency + '18', color: urgency, padding: '0.2rem 0.6rem', borderRadius: '20px', fontWeight: '700', fontSize: '0.8rem' }}>{daysLeft}d</span>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem 1.5rem', fontSize: '0.85rem' }}>
                        <span style={{ color: 'rgba(255,255,255,0.6)' }}>€{fmt(loan.capital)} al {loan.interest_rate}%</span>
                        <span style={{ color: 'rgba(255,255,255,0.5)' }}>{loan.end_date ? new Date(loan.end_date).toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.5rem', fontSize: '0.85rem' }}>
                        <span style={{ color: '#f97316' }}>{i18n.language === 'ca' ? 'Interessos' : 'Intereses'}: €{fmt(interest, 2)}</span>
                        <span style={{ color: '#dc2626', fontWeight: '700' }}>{i18n.language === 'ca' ? 'Total' : 'Total'}: €{fmt(loan.total_return || loan.capital, 2)}</span>
                      </div>
                    </div>
                  );
                })}
                {/* TOTAL */}
                <div style={{ padding: '0.75rem 1rem', borderTop: '2px solid rgba(96,165,250,0.3)', display: 'flex', justifyContent: 'space-between', fontSize: '0.95rem' }}>
                  <span style={{ fontWeight: '700', color: '#60a5fa' }}>TOTAL: €{fmt(totalCapital)}</span>
                  <span style={{ fontWeight: '700', color: '#dc2626' }}>€{fmt(totalToReturn, 2)}</span>
                </div>
              </div>
            </div>
          )}

          {/* === Matured/Finalized Loans === */}
          {maturedLoans.length > 0 && (
            <div style={{ background: 'rgba(107,114,128,0.08)', borderRadius: '16px', padding: '1rem', marginBottom: '1.5rem', border: '1px solid rgba(107,114,128,0.2)' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: '700', color: '#9ca3af', marginBottom: '0.75rem' }}>
                {i18n.language === 'ca' ? 'Préstecs Finalitzats' : 'Préstamos Finalizados'} ({maturedLoans.length})
              </h3>
              {maturedLoans.map(loan => (
                <div key={loan._id} onClick={() => navigate(`/loans/${loan._id}`)} style={{
                  padding: '0.75rem', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', cursor: 'pointer', marginBottom: '0.5rem'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                    <span style={{ fontWeight: '600', color: '#9ca3af' }}>{loan.lender_name || '?'}</span>
                    <span style={{ color: '#6b7280', fontWeight: '600' }}>{t('status.matured')}</span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem 1.5rem', fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)' }}>
                    <span>€{fmt(loan.capital)} al {loan.interest_rate}%</span>
                    {loan.final_interest_amount && (
                      <span style={{ color: '#60a5fa' }}>{i18n.language === 'ca' ? 'Interessos pagats' : 'Intereses pagados'}: €{fmt(loan.final_interest_amount, 2)}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* === Cancelled Loans Summary === */}
          {cancelledLoans.length > 0 && (
            <div style={{ background: 'rgba(239,68,68,0.06)', borderRadius: '16px', padding: '1rem', marginBottom: '1.5rem', border: '1px solid rgba(239,68,68,0.2)' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: '700', color: '#f87171', marginBottom: '0.75rem' }}>
                {i18n.language === 'ca' ? 'Préstecs Cancel·lats' : 'Préstamos Cancelados'} ({cancelledLoans.length})
              </h3>
              {cancelledLoans.map(loan => (
                <div key={loan._id} onClick={() => navigate(`/loans/${loan._id}`)} style={{
                  padding: '0.75rem', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', cursor: 'pointer', marginBottom: '0.5rem'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                    <span style={{ fontWeight: '600', color: '#f87171' }}>{loan.lender_name || '?'}</span>
                    <span style={{ color: '#ef4444', fontWeight: '700' }}>{t('status.cancelled')}</span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem 1.5rem', fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)' }}>
                    <span>€{fmt(loan.capital)} al {loan.cancellation_request?.adjusted_interest_rate || loan.interest_rate}%</span>
                    {loan.final_interest_amount && (
                      <span style={{ color: '#fbbf24' }}>{i18n.language === 'ca' ? 'Interessos finals' : 'Intereses finales'}: €{fmt(loan.final_interest_amount, 2)}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* === ROW 3: Monthly Payment Projection + Charts === */}
          <div style={{ display: 'grid', gridTemplateColumns: loans.length > 0 ? '2fr 1fr' : '1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
            {/* Monthly Projection Bar Chart */}
            {getMonthlyProjection().length > 0 && (
              <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '16px', padding: '1.5rem', border: '1px solid rgba(255,255,255,0.08)' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: '700', color: '#60a5fa', marginBottom: '1rem' }}>
                  {i18n.language === 'ca' ? 'Pagaments per Mes de Venciment' : 'Pagos por Mes de Vencimiento'}
                </h3>
              <div ref={barChartRef}>
                <BarChart width={barChartWidth} height={230} data={getMonthlyProjection()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'rgba(255,255,255,0.5)' }} />
                    <YAxis tick={{ fontSize: 11, fill: 'rgba(255,255,255,0.5)' }} tickFormatter={v => `€${(v/1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => [`€${v.toLocaleString(locale, {minimumFractionDigits:2})}`, '']} />
                    <Bar dataKey="capital" name="Capital" fill="#103766" radius={[4,4,0,0]} />
                    <Bar dataKey="interests" name={i18n.language === 'ca' ? 'Interessos' : 'Intereses'} fill="#f97316" radius={[4,4,0,0]} />
                </BarChart>
              </div>
                {/* Legend + Monthly totals */}
                <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.75rem', fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)' }}>
                  <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#103766', borderRadius: 2, marginRight: 4 }}></span>Capital</span>
                  <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#f97316', borderRadius: 2, marginRight: 4 }}></span>{i18n.language === 'ca' ? 'Interessos' : 'Intereses'}</span>
                </div>
              </div>
            )}

            {/* Status Pie Chart */}
            <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '16px', padding: '1.5rem', border: '1px solid rgba(255,255,255,0.08)' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '700', color: '#60a5fa', marginBottom: '1rem' }}>
                {i18n.language === 'ca' ? 'Distribució' : 'Distribución'}
              </h3>
              {statusData.length > 0 ? (
                <div ref={pieChartRef}>
                  <PieChart width={Math.min(pieChartWidth, 300)} height={200}>
                    <Pie data={statusData} cx="50%" cy="50%" outerRadius={70} dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`} labelLine={false}>
                      {statusData.map((e, i) => <Cell key={i} fill={e.color} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </div>
              ) : (
                <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.4)', padding: '3rem 0' }}>{i18n.language === 'ca' ? 'No hi ha dades' : 'No hay datos'}</p>
              )}
              {/* Quick stats under pie */}
              <div style={{ display: 'grid', gap: '0.5rem', marginTop: '0.5rem' }}>
                {[
                  { label: i18n.language === 'ca' ? 'Capital Mitjà' : 'Capital Medio', value: `€${fmt(activeLoans.length > 0 ? totalCapital / activeLoans.length : 0)}` },
                  { label: i18n.language === 'ca' ? 'Taxa Signatura' : 'Tasa Firma', value: `${loans.length > 0 ? ((activeLoans.length / loans.length) * 100).toFixed(0) : 0}%` },
                ].map(s => (
                  <div key={s.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0.75rem', background: 'rgba(255,255,255,0.03)', borderRadius: '8px' }}>
                    <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)' }}>{s.label}</span>
                    <span style={{ fontSize: '0.85rem', fontWeight: '700', color: '#60a5fa' }}>{s.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* === ROW 4: Referral Bonuses === */}
          {bonusesSummary && bonusesSummary.lenders_with_bonuses > 0 && (
            <div style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.1) 0%, rgba(5,150,105,0.05) 100%)', borderRadius: '16px', padding: '1.5rem', marginBottom: '1.5rem', border: '1px solid rgba(16,185,129,0.3)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: '700', color: '#34d399', margin: 0 }}>
                  {i18n.language === 'ca' ? 'Bonus Referits - Detall Pagaments' : 'Bonus Referidos - Detalle Pagos'}
                </h3>
                <div style={{ textAlign: 'right' }}>
                  <p style={{ fontSize: '1.3rem', fontWeight: '700', color: '#34d399', margin: 0 }}>€{fmt(bonusesSummary.total_bonuses_gross, 2)}</p>
                  <p style={{ fontSize: '0.75rem', color: 'rgba(16,185,129,0.6)', margin: 0 }}>Net: €{fmt(bonusesSummary.total_bonuses_net, 2)}</p>
                </div>
              </div>
              <div style={{ display: 'grid', gap: '1rem' }}>
                {bonusesSummary.bonuses_detail?.map((b, idx) => (
                  <div key={idx} style={{ padding: '1rem', background: 'rgba(16,185,129,0.05)', borderRadius: '12px', border: '1px solid rgba(16,185,129,0.15)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <div>
                        <span style={{ fontWeight: '700', color: '#34d399', fontSize: '0.95rem' }}>{b.lender_name}</span>
                        <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem', marginLeft: '0.5rem' }}>{b.lender_email}</span>
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <div style={{ background: 'rgba(0,0,0,0.2)', padding: '0.5rem', borderRadius: '8px' }}>
                        <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)' }}>{i18n.language === 'ca' ? 'Bonus Acumulat' : 'Bonus Acumulado'}</div>
                        <div style={{ fontSize: '1rem', fontWeight: '700', color: '#34d399' }}>€{fmt(b.bonus_accumulated || b.bonus_gross, 2)}</div>
                      </div>
                      <div style={{ background: 'rgba(0,0,0,0.2)', padding: '0.5rem', borderRadius: '8px' }}>
                        <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)' }}>IRPF 19%</div>
                        <div style={{ fontSize: '1rem', fontWeight: '700', color: '#f87171' }}>-€{fmt(b.bonus_retention || (b.bonus_accumulated || b.bonus_gross) * 0.19, 2)}</div>
                      </div>
                      <div style={{ background: 'rgba(0,0,0,0.2)', padding: '0.5rem', borderRadius: '8px' }}>
                        <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)' }}>{i18n.language === 'ca' ? 'Net a Pagar' : 'Neto a Pagar'}</div>
                        <div style={{ fontSize: '1rem', fontWeight: '700', color: '#60a5fa' }}>€{fmt(b.bonus_net || (b.bonus_accumulated || b.bonus_gross) * 0.81, 2)}</div>
                      </div>
                    </div>
                    {/* Per-referred detail */}
                    {b.referred_detail && b.referred_detail.length > 0 && (
                      <div style={{ borderTop: '1px solid rgba(16,185,129,0.15)', paddingTop: '0.5rem', marginTop: '0.25rem' }}>
                        {b.referred_detail.map((ref, ri) => (
                          <div key={ri} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.25rem 0', fontSize: '0.8rem' }}>
                            <span style={{ color: 'rgba(255,255,255,0.7)' }}>
                              {ref.name} 
                              {ref.status === 'cancelled' && <span style={{ color: '#f87171', marginLeft: '0.3rem' }}>({i18n.language === 'ca' ? 'Cancel·lat' : 'Cancelado'})</span>}
                            </span>
                            <span style={{ color: '#34d399', fontWeight: '600' }}>€{fmt(ref.bonus_accumulated, 2)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* === ROW 5: Total Debt Summary Box === */}
          {activeLoans.length > 0 && (
            <div style={{ background: 'linear-gradient(135deg, rgba(220,38,38,0.1) 0%, rgba(185,28,28,0.05) 100%)', borderRadius: '16px', padding: '1.5rem', marginBottom: '1.5rem', border: '1px solid rgba(220,38,38,0.3)' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '700', color: '#f87171', marginBottom: '1rem' }}>
                {i18n.language === 'ca' ? 'Resum Total Obligacions' : 'Resumen Total Obligaciones'}
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
                <div style={{ background: 'rgba(255,255,255,0.04)', padding: '1rem', borderRadius: '12px' }}>
                  <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)' }}>{i18n.language === 'ca' ? 'Capital a Tornar' : 'Capital a Devolver'}</p>
                  <p style={{ fontSize: '1.4rem', fontWeight: '700', color: '#60a5fa' }}>€{fmt(totalCapital)}</p>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.04)', padding: '1rem', borderRadius: '12px' }}>
                  <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)' }}>{i18n.language === 'ca' ? 'Interessos a Pagar' : 'Intereses a Pagar'}</p>
                  <p style={{ fontSize: '1.4rem', fontWeight: '700', color: '#f97316' }}>€{fmt(totalInterestPending, 2)}</p>
                </div>
                {totalBonuses > 0 && (
                  <div style={{ background: 'rgba(255,255,255,0.04)', padding: '1rem', borderRadius: '12px' }}>
                    <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)' }}>{t('referral.bonus')}</p>
                    <p style={{ fontSize: '1.4rem', fontWeight: '700', color: '#10b981' }}>€{fmt(totalBonuses, 2)}</p>
                  </div>
                )}
                <div style={{ background: '#991b1b', padding: '1rem', borderRadius: '12px' }}>
                  <p style={{ fontSize: '0.75rem', color: '#fecaca' }}>TOTAL</p>
                  <p style={{ fontSize: '1.6rem', fontWeight: '700', color: 'white' }}>€{fmt(totalDebt, 2)}</p>
                </div>
              </div>
            </div>
          )}

          {/* Empty state */}
          {loans.length === 0 && (
            <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '16px', padding: '4rem', textAlign: 'center', border: '1px solid rgba(255,255,255,0.08)' }}>
              <p style={{ fontSize: '3rem', marginBottom: '1rem' }}>📊</p>
              <p style={{ fontSize: '1.25rem', fontWeight: '600', color: '#60a5fa', marginBottom: '0.5rem' }}>
                {i18n.language === 'ca' ? 'No hi ha préstecs encara' : 'No hay préstamos todavía'}
              </p>
              <p style={{ color: 'rgba(255,255,255,0.5)' }}>
                {i18n.language === 'ca' ? 'Crea el primer préstec des de la secció Préstecs' : 'Crea el primer préstamo desde la sección Préstamos'}
              </p>
              <button onClick={() => navigate('/admin/loans')} style={{ marginTop: '1rem', padding: '0.75rem 2rem', background: '#103766', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '600', cursor: 'pointer' }}>
                {t('buttons.createLoan')}
              </button>
            </div>
          )}

        </main>
      </div>
    </div>
  );
}

export default AdminDashboard;
